import React, { useEffect, useState, useMemo } from 'react';
import DefaultModal from '../../../components/default/DefaultModal';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import LazyLoading from '../../../components/LazyLoading';
import {
  EmpacarDataResponse,
  EmpacarOutputItem,
  EmpacarRequirementGroup,
  EmpacarSpecifiedProduct,
  EmpacarOutputPayload,
  EmpacarExecutePayload,
} from '../../../app/models/PackingOrder';

/**
 * Estado del formulario de empacar.
 * consumptions: batch_id -> quantity (acumulado entre todos los productos especificados)
 */
type FormStateByOutput = {
  quantityElaborated: number;
  consumptions: Record<number, number>;
};

type Props = {
  show: boolean;
  packingOrderId: number;
  empacarData: EmpacarDataResponse | null;
  loadingData: boolean;
  loadingExecute: boolean;
  onLoadData: () => void;
  onSubmit: (body: EmpacarExecutePayload) => void;
  onClose: () => void;
};

/** Distribuye cantidad requerida entre lotes en orden FIFO */
function fifoConsumptions(
  required: number,
  batches: Array<{ batch_id: number; available_quantity: number }>
): Record<number, number> {
  const result: Record<number, number> = {};
  let remaining = required;
  for (const b of batches) {
    if (remaining <= 0) break;
    const take = Math.min(remaining, Number(b.available_quantity));
    if (take > 0) {
      result[b.batch_id] = (result[b.batch_id] || 0) + take;
      remaining -= take;
    }
  }
  return result;
}

/** Suma todos los lotes de los productos especificados de un grupo para FIFO automático */
function buildGroupFifoConsumptions(
  group: EmpacarRequirementGroup,
  qty: number
): Record<number, number> {
  const allConsumptions: Record<number, number> = {};
  const requiredForGroup = group.required_quantity_per_unit * qty;
  let remaining = requiredForGroup;

  for (const sp of group.specified_products) {
    if (remaining <= 0) break;
    const spRequired = Math.min(sp.quantity_per_unit * qty, remaining);
    const assigned = fifoConsumptions(spRequired, sp.batches);
    for (const [batchId, q] of Object.entries(assigned)) {
      allConsumptions[Number(batchId)] = (allConsumptions[Number(batchId)] || 0) + q;
      remaining -= q;
    }
  }

  return allConsumptions;
}

const EmpacarModal: React.FC<Props> = ({
  show,
  packingOrderId,
  empacarData,
  loadingData,
  loadingExecute,
  onLoadData,
  onSubmit,
  onClose,
}) => {
  const [formByOutput, setFormByOutput] = useState<Record<number, FormStateByOutput>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [movementDate, setMovementDate] = useState<string>('');

  useEffect(() => {
    if (show && packingOrderId) {
      onLoadData();
      const today = new Date();
      setMovementDate(
        `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
      );
    }
    if (!show) setSubmitError(null);
  }, [show, packingOrderId]);

  useEffect(() => {
    if (!empacarData?.outputs) {
      setFormByOutput({});
      return;
    }
    const initial: Record<number, FormStateByOutput> = {};
    empacarData.outputs.forEach((o) => {
      initial[o.output_product_id] = { quantityElaborated: 0, consumptions: {} };
    });
    setFormByOutput(initial);
  }, [empacarData]);

  /** Recalcula los consumos FIFO cuando cambia la cantidad elaborada */
  const setQuantityElaborated = (outputProductId: number, value: number) => {
    const output = empacarData?.outputs?.find((o) => o.output_product_id === outputProductId);
    if (!output || value < 0) return;

    setFormByOutput((prev) => {
      const consumptions: Record<number, number> = {};
      for (const group of output.requirement_groups) {
        const assigned = buildGroupFifoConsumptions(group, value);
        Object.assign(consumptions, assigned);
      }
      return {
        ...prev,
        [outputProductId]: { quantityElaborated: value, consumptions },
      };
    });
  };

  const setBatchQuantity = (outputProductId: number, batchId: number, value: number) => {
    setFormByOutput((prev) => {
      const current = prev[outputProductId] || { quantityElaborated: 0, consumptions: {} };
      const consumptions = { ...current.consumptions };
      if (value <= 0) delete consumptions[batchId];
      else consumptions[batchId] = value;
      return { ...prev, [outputProductId]: { ...current, consumptions } };
    });
  };

  const buildPayload = (): EmpacarOutputPayload[] => {
    if (!empacarData?.outputs) return [];
    return empacarData.outputs
      .filter((o) => (formByOutput[o.output_product_id]?.quantityElaborated ?? 0) > 0)
      .map((o) => {
        const state = formByOutput[o.output_product_id];
        const consumptions = Object.entries(state?.consumptions || {})
          .filter(([, q]) => Number(q) > 0)
          .map(([batch_id, quantity]) => ({ batch_id: Number(batch_id), quantity: Number(quantity) }));
        return {
          output_product_id: o.output_product_id,
          quantity_elaborated: state!.quantityElaborated,
          consumptions,
        };
      });
  };

  const validate = (): string | null => {
    if (!empacarData?.outputs) return 'No hay datos de empaque.';

    for (const output of empacarData.outputs) {
      const state = formByOutput[output.output_product_id];
      const qty = state?.quantityElaborated ?? 0;
      if (qty <= 0) continue;

      for (const group of output.requirement_groups) {
        const requiredTotal = group.required_quantity_per_unit * qty;

        // Sumar consumo de todos los lotes de los productos especificados en este grupo
        let assignedTotal = 0;
        for (const sp of group.specified_products) {
          for (const b of sp.batches) {
            assignedTotal += state?.consumptions[b.batch_id] ?? 0;
          }
        }

        if (assignedTotal < requiredTotal - 0.0001) {
          const groupLabel = [group.category_name, group.quality_name, group.unit_name]
            .filter(Boolean)
            .join(' / ') || group.group_key;
          return `Grupo "${groupLabel}": consumo asignado (${assignedTotal}) es menor al requerido (${requiredTotal}).`;
        }

        // Verificar que ningún lote supere su disponibilidad
        for (const sp of group.specified_products) {
          for (const b of sp.batches) {
            const taken = state?.consumptions[b.batch_id] ?? 0;
            if (taken > b.available_quantity + 0.0001) {
              return `Lote "${b.batch_name}" no tiene stock suficiente (disponible: ${b.available_quantity}, asignado: ${taken}).`;
            }
          }
        }
      }
    }

    const payload = buildPayload();
    if (payload.length === 0) return 'Indique al menos un producto con cantidad elaborada mayor a cero.';
    return null;
  };

  const handleSubmit = () => {
    setSubmitError(null);
    const err = validate();
    if (err) {
      setSubmitError(err);
      return;
    }
    onSubmit({ movement_date: movementDate, outputs: buildPayload() });
  };

  const payload = useMemo(() => buildPayload(), [formByOutput, empacarData]);
  const canSubmit = payload.length > 0;

  return (
    <DefaultModal
      show={show}
      handleClose={onClose}
      title="Empacar"
      size="lg"
      backdrop
      showFooter={false}
    >
      {loadingData ? (
        <LazyLoading height={200} />
      ) : !empacarData?.outputs?.length ? (
        <p className="text-muted mb-0">
          No hay productos resultado o no tienen receta / insumos especificados. Agregue productos
          resultado, defina sus recetas y especifique los insumos.
        </p>
      ) : (
        <>
          <div className="mb-3 small text-muted">
            Indique la cantidad elaborada por producto resultado. Se sugiere el consumo de lotes en
            orden FIFO según los insumos especificados. Puede ajustar las cantidades por lote.
          </div>
          <div className="mb-3 d-flex align-items-center gap-2">
            <label className="mb-0 small">Fecha real del movimiento:</label>
            <input
              type="date"
              className="form-control form-control-sm"
              style={{ maxWidth: 180 }}
              value={movementDate}
              onChange={(e) => setMovementDate(e.target.value)}
            />
          </div>

          {empacarData.outputs.map((output: EmpacarOutputItem) => {
            const qty = formByOutput[output.output_product_id]?.quantityElaborated ?? 0;

            return (
              <div key={output.output_product_id} className="border rounded p-3 mb-3">
                {/* Encabezado del producto resultado */}
                <div className="d-flex align-items-center gap-3 mb-3">
                  <strong>{output.output_product_name}</strong>
                  <label className="mb-0 d-flex align-items-center gap-1 small">
                    Cantidad elaborada:
                    <input
                      type="number"
                      min={0}
                      step={1}
                      className="form-control form-control-sm ms-1"
                      style={{ width: 90 }}
                      value={formByOutput[output.output_product_id]?.quantityElaborated ?? ''}
                      onChange={(e) =>
                        setQuantityElaborated(output.output_product_id, Number(e.target.value) || 0)
                      }
                    />
                    {output.output_unit_name && <span>{output.output_unit_name}</span>}
                  </label>
                </div>

                {qty > 0 &&
                  output.requirement_groups.map((group: EmpacarRequirementGroup) => {
                    const groupLabel =
                      [group.category_name, group.quality_name, group.unit_name]
                        .filter(Boolean)
                        .join(' / ') || group.group_key;
                    const requiredTotal = group.required_quantity_per_unit * qty;

                    if (group.specified_products.length === 0) {
                      return (
                        <div key={group.group_key} className="ms-3 mb-2 small text-warning">
                          <i className="mdi mdi-alert-circle" /> Grupo &quot;{groupLabel}&quot; sin
                          productos especificados — agréguelos en la sección de Insumos.
                        </div>
                      );
                    }

                    return (
                      <div key={group.group_key} className="ms-2 mb-3">
                        <div className="text-muted small fw-semibold mb-1">
                          {groupLabel} — Requerido total:{' '}
                          <strong>
                            {requiredTotal} {group.unit_name ?? ''}
                          </strong>
                        </div>

                        {group.specified_products.map((sp: EmpacarSpecifiedProduct) => {
                          const spRequired = sp.quantity_per_unit * qty;
                          return (
                            <div key={sp.input_id} className="ms-3 mb-2 small">
                              <div className="text-muted mb-1">
                                {sp.product_name} — Por unidad: {sp.quantity_per_unit}{' '}
                                {sp.unit_name ?? ''} → Total: {spRequired} {sp.unit_name ?? ''}
                              </div>
                              {sp.batches.length === 0 ? (
                                <p className="text-warning small">
                                  Sin lotes disponibles para {sp.product_name}.
                                </p>
                              ) : (
                                <div className="table-responsive">
                                  <table className="table table-sm table-bordered mb-0">
                                    <thead>
                                      <tr>
                                        <th>Almacén</th>
                                        <th>Lote</th>
                                        <th>Disponible</th>
                                        <th>Cantidad a usar</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {sp.batches.map((b) => {
                                        const taken =
                                          formByOutput[output.output_product_id]?.consumptions[
                                            b.batch_id
                                          ] ?? 0;
                                        const overStock = taken > b.available_quantity + 0.0001;
                                        return (
                                          <tr
                                            key={b.batch_id}
                                            className={overStock ? 'table-danger' : ''}
                                          >
                                            <td>{b.warehouse_name ?? '—'}</td>
                                            <td>
                                              {b.batch_name?.toLowerCase() !== 'null' &&
                                              b.batch_name?.trim()
                                                ? b.batch_name
                                                : `Lote ${b.batch_id}`}
                                            </td>
                                            <td>
                                              {b.available_quantity} {b.unit_name ?? ''}
                                            </td>
                                            <td>
                                              <input
                                                type="number"
                                                min={0}
                                                step={0.01}
                                                className={`form-control form-control-sm ${overStock ? 'is-invalid' : ''}`}
                                                value={taken || ''}
                                                onChange={(e) =>
                                                  setBatchQuantity(
                                                    output.output_product_id,
                                                    b.batch_id,
                                                    Number(e.target.value) || 0
                                                  )
                                                }
                                              />
                                              {overStock && (
                                                <div className="invalid-feedback" style={{ display: 'flex' }}>
                                                  Supera el stock disponible.
                                                </div>
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
              </div>
            );
          })}

          {submitError && (
            <div className="alert alert-danger py-2 mb-2" role="alert">
              {submitError}
            </div>
          )}
          <hr />
          <div className="d-flex justify-content-end gap-2">
            <ButtonCancelForm callbackCancel={onClose} locked={loadingExecute} />
            <ButtonSaveForm
              callbackSave={handleSubmit}
              locked={loadingExecute || !canSubmit}
            />
          </div>
        </>
      )}
    </DefaultModal>
  );
};

export default EmpacarModal;
